import { lazy, Suspense } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Menu, Loader2 } from 'lucide-react';
import { ModelTab, ModelMetrics } from '@/components/model';
import { ProfileTab } from '@/components/profile';
import { AccountsTab } from '@/components/accounts';
import { PrivacyTab } from '@/components/privacy';
import { SettingsLayout } from '@/components/settings';
import { DashboardOverview } from './DashboardOverview';
import { DashboardSidebar } from './DashboardSidebar';
import { DetailView } from '../DetailView';
import { HoldingsTable } from '../HoldingsTable';
import { LinkedAccountsPanel } from '../LinkedAccountsPanel';
import { DIAGNOSTIC_CATEGORIES } from '@/lib/constants';

// Lazy load heavy components
const EducationHub = lazy(() => import('@/components/education').then(m => ({ default: m.EducationHub })));

// Loading fallback for charts
function ChartLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64 bg-muted/30 rounded-lg">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

interface DashboardDesktopViewProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  selectedCategory: string | null;
  selectedCard: any;
  analysis: any;
  clientInfo: any;
  scoringConfig: any;
  checklist: any;
  lifetimeIncomeInputs: any;
  assumptions: any;
  partnerView: string;
  partner: any;
  sampleAccounts: any[];
  onSelectCategory: (key: string | null) => void;
  onChecklistUpdate: any;
  onLifetimeIncomeUpdate: any;
  observationCards: any[];
  noIssuesCards: any[];
  snapshotData: any;
  cardContracts: any[];
  actionPlan: any;
  onClientSelect?: any;
  holdings: any[];
  onHoldingsUpdate: any;
  onModelMetricsChange?: (metrics: ModelMetrics | null) => void;
}

export function DashboardDesktopView({
  activeTab,
  setActiveTab,
  sidebarOpen,
  setSidebarOpen,
  selectedCategory,
  selectedCard,
  analysis,
  clientInfo,
  scoringConfig,
  checklist,
  lifetimeIncomeInputs,
  assumptions,
  partnerView,
  partner,
  sampleAccounts,
  onSelectCategory,
  onChecklistUpdate,
  onLifetimeIncomeUpdate,
  observationCards,
  noIssuesCards,
  snapshotData,
  cardContracts,
  actionPlan,
  onClientSelect,
  holdings,
  onHoldingsUpdate,
  onModelMetricsChange,
}: DashboardDesktopViewProps) {
  
  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
      {/* Mobile sidebar trigger for tablets - tabs are now in Header */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="lg:hidden shrink-0">
            <Menu className="h-4 w-4" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[320px] sm:w-[400px] overflow-y-auto">
          <div className="pt-6">
            <DashboardSidebar
              sampleAccounts={sampleAccounts}
              clientInfo={clientInfo}
              onClientSelect={(client) => {
                onClientSelect?.(client);
                setSidebarOpen(false);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <TabsContent value="observations" className="space-y-4 sm:space-y-6">
        {selectedCategory && (selectedCard || analysis.diagnostics[selectedCategory as keyof typeof analysis.diagnostics]) ? (
          <DetailView
            name={selectedCard?.title ?? DIAGNOSTIC_CATEGORIES[selectedCategory as keyof typeof DIAGNOSTIC_CATEGORIES]?.name ?? selectedCategory}
            categoryKey={selectedCategory}
            result={selectedCard 
              ? {
                  status: selectedCard.status,
                  score: selectedCard.score,
                  keyFinding: selectedCard.keyFinding,
                  headlineMetric: selectedCard.headlineMetric,
                  details: selectedCard.details,
                }
              : analysis.diagnostics[selectedCategory as keyof typeof analysis.diagnostics]
            }
            onClose={() => onSelectCategory(null)}
            scoringConfig={scoringConfig}
            riskTolerance={clientInfo.riskTolerance}
            clientAge={clientInfo.currentAge}
            inflationRate={assumptions.inflationRate}
            checklist={checklist}
            onChecklistUpdate={onChecklistUpdate}
            lifetimeIncomeInputs={lifetimeIncomeInputs}
            onLifetimeIncomeUpdate={onLifetimeIncomeUpdate}
            card={selectedCard}
          />
        ) : (
          <DashboardOverview
            sampleAccounts={sampleAccounts}
            observationCards={observationCards}
            noIssuesCards={noIssuesCards}
            scoringConfig={scoringConfig}
            clientInfo={clientInfo}
            onSelectCategory={onSelectCategory}
            snapshotData={snapshotData}
            partnerView={partnerView}
            partner={partner}
            cardContracts={cardContracts}
            actionPlan={actionPlan}
            planningProgress={
              analysis.diagnostics?.planningGaps?.details
                ? {
                    completed: (analysis.diagnostics.planningGaps.details as any).completed ?? 0,
                    total: (analysis.diagnostics.planningGaps.details as any).total ?? 11,
                  }
                : undefined
            }
            onClientSelect={(client) => {
              onClientSelect?.(client);
              setSidebarOpen(false);
            }}
          />
        )}
      </TabsContent>

      <TabsContent value="model" className="mt-0">
        <ModelTab
          portfolioValue={snapshotData.totalValue}
          portfolioExpectedReturn={snapshotData.expectedReturn}
          portfolioVolatility={snapshotData.volatility}
          onMetricsChange={onModelMetricsChange}
        />
      </TabsContent>

      <TabsContent value="holdings" className="space-y-4 sm:space-y-6">
        {/* Linked Accounts - consolidated in Holdings tab */}
        <LinkedAccountsPanel onHoldingsSync={() => {}} sampleAccounts={sampleAccounts} />

        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-base sm:text-lg">Portfolio Holdings</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <HoldingsTable holdings={holdings} onUpdate={onHoldingsUpdate} sampleAccounts={sampleAccounts} onNavigateToAccounts={() => setActiveTab('accounts')} />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="learn" className="space-y-4 sm:space-y-6">
        <Suspense fallback={<ChartLoadingFallback />}>
          <EducationHub />
        </Suspense>
      </TabsContent>

      <TabsContent value="profile" className="space-y-4 sm:space-y-6">
        <SettingsLayout>
          <ProfileTab />
        </SettingsLayout>
      </TabsContent>

      <TabsContent value="accounts" className="space-y-4 sm:space-y-6">
        <SettingsLayout>
          <AccountsTab sampleAccounts={sampleAccounts} />
        </SettingsLayout>
      </TabsContent>

      <TabsContent value="privacy" className="space-y-4 sm:space-y-6">
        <SettingsLayout>
          <PrivacyTab />
        </SettingsLayout>
      </TabsContent>

      {/* Chatbot Mount Point - Intercom-style sidebar trigger (Phase 8) */}
      <div
        id="chatbot-mount-point-desktop"
        className="fixed bottom-6 right-6 z-40"
        aria-label="AI Assistant"
      >
        {/* ChatbotSidebar will be mounted here in Phase 8 */}
      </div>
    </Tabs>
  );
}
